import React from 'react';
import { observer } from 'mobx-react';
import { WebStorage } from '../storage';
import { EventSystem } from './events';

const SUPPORT_BOARD_WIDGET_SRC = 'https://support.rocketman-streams.com:3001/widget.js';
const SUPPORT_BOARD_PUBLIC_KEY = 'BEuLUMq9BUUsPcy8fqEotDp7CxruSmQR';

const ensureQueue = (): SupportBoardApi => {
    if (!window.SupportBoard) {
        window.SupportBoard = { q: [] };
    }

    if (!Array.isArray(window.SupportBoard.q)) {
        window.SupportBoard.q = [];
    }

    return window.SupportBoard;
};

const callSupportBoard = (...command: SupportBoardCommand) => {
    const board = ensureQueue();
    const method = command[0];
    const fn = board[method];

    if (typeof fn === 'function') {
        (fn as (...args: unknown[]) => void)(...command.slice(1));
        return;
    }

    if (board.ready) {
        return;
    }

    board.q.push(command);
};

const setLauncherHidden = (hidden: boolean) => {
    const board = ensureQueue();

    if (typeof board.setHideLauncher === 'function') {
        board.setHideLauncher(hidden);
        return;
    }

    callSupportBoard('setHideLauncher', hidden);

    const launcher = document.querySelector('.sbw-root .sbw-launcher') as HTMLElement | null;

    if (!launcher) {
        return;
    }

    launcher.style.setProperty('display', hidden ? 'none' : 'inline-flex', 'important');
};

export const openSupportBoard = () => {
    callSupportBoard('open');
    setLauncherHidden(false);
};

let scriptInjected = false;

const injectWidget = () => {
    if (scriptInjected || document.querySelector('script[data-support-board-widget]')) {
        scriptInjected = true;
        return;
    }

    scriptInjected = true;
    ensureQueue();

    const script = document.createElement('script');
    script.src = SUPPORT_BOARD_WIDGET_SRC;
    script.async = true;
    script.dataset.key = SUPPORT_BOARD_PUBLIC_KEY;
    script.dataset.hideLauncher = '1';
    script.dataset.supportBoardWidget = '1';
    document.body.appendChild(script);
};

export const SupportBoardWidget = observer(() => {
    const { userData, lang } = WebStorage;

    React.useEffect(() => {
        injectWidget();
        callSupportBoard('setLocaleSwitcher', false);
        setLauncherHidden(true);

        const listener = EventSystem.on('openSupportBoard', () => {
            openSupportBoard();
        });

        let lastOpen = !!window.SupportBoard?.isOpen;
        const timer = window.setInterval(() => {
            const open = !!window.SupportBoard?.isOpen;

            if (open === lastOpen) {
                return;
            }

            lastOpen = open;
            setLauncherHidden(!open);
        }, 200);

        return () => {
            listener?.destroy();
            window.clearInterval(timer);
        };
    }, []);

    React.useEffect(() => {
        callSupportBoard('setLocale', lang);
    }, [lang]);

    React.useEffect(() => {
        if (!userData?.id || !userData.token) {
            return;
        }

        let cancelled = false;

        WebStorage.ApiRequest('/api/supportBoard/visitor').then((res) => {
            if (cancelled || !res.status) {
                return;
            }

            callSupportBoard('setVisitor', {
                name: res.name,
                clientId: res.clientId,
                clientToken: res.clientToken,
                custom: {
                    ...(res.custom || {}),
                    locale: WebStorage.lang,
                },
            });
        });

        return () => {
            cancelled = true;
        };
    }, [userData?.id, userData?.token, userData?.name]);

    return null;
});
